import { useQuery } from "@tanstack/react-query";
import { FC, SVGProps } from "react";
import { useSearchParams } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import {
  getPlatform,
  getPlatformUserMemberships,
  getPlatformUserPackages,
  getPromotions,
  getRetailproductsByPlatformIdOrders,
  getRewards,
  getUser,
} from "api/methods";
import { RewardStatus } from "api/types";
import DiscoverNav from "atoms/icon/icons/discover_nav.svg";
import HomeNav from "atoms/icon/icons/home_nav.svg";
import DiscoverPlus1Nav from "atoms/icon/icons/ic_discover+1_nav_ol.svg";
import WalletPlus1Nav from "atoms/icon/icons/ic_wallet+1_nav_ol.svg";
import RewardsPlus1Nav from "atoms/icon/icons/rewards+1_nav.svg";
import RewardsNav from "atoms/icon/icons/rewards_nav.svg";
import ShopNav from "atoms/icon/icons/shop_nav.svg";
import WalletNav from "atoms/icon/icons/wallet_nav.svg";
import { useIsAnonymousBrowsing } from "modules/anonymousBrowsing";
import { hasAnyOffersToShow, hasAvailableTreatmentsToSelect } from "pages/root/wallet/utils";
import {
  preloadDiscover,
  preloadHome,
  preloadRewards,
  preloadShop,
  preloadWallet,
} from "modules/routing/primaryRoutePreload";

export type PrimaryNavItem = {
  /** Stable identity for the tab, independent of the (translated) label. */
  key: "home" | "discover" | "shop" | "rewards" | "wallet";

  /** Where the tab navigates to. */
  to: string;

  /** Translated label. */
  label: string;

  /** Tab icon, already resolved to the badged variant when there is something new. */
  svg: FC<SVGProps<SVGSVGElement>>;

  /** True when the tab has something new to show — a dot on surfaces without badged icons. */
  hasBadge: boolean;

  /** Override the active state (used for custom navigation scenarios). */
  isActiveOverride?: boolean;

  intentPreload?: () => void;
};

/**
 * The app's primary destinations, in order, filtered by platform settings and
 * what the user actually owns.
 *
 * Shared by the mobile bottom bar (`Footer`) and the desktop/tablet header
 * (`DesktopHeader`) so the two navigations can never drift apart.
 */
export const usePrimaryNavItems = (): PrimaryNavItem[] => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const fromWallet = searchParams.get("from") === "wallet";
  const isAnonymousBrowsing = useIsAnonymousBrowsing();

  const { data: rewards } = useQuery({ ...getRewards.keyed({}) });

  const { data: platform } = useQuery({ ...getPlatform.keyed() });

  const { data: user } = useQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const promotions = useQuery({
    ...getPromotions.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const userMemberships = useQuery({
    ...getPlatformUserMemberships.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const { data: treatments } = useQuery({
    ...getPlatformUserPackages.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const { data: productOrders } = useQuery({
    ...getRetailproductsByPlatformIdOrders.keyed({
      platformId: platform?.id ?? "",
    }),
    queryFn: async () => {
      if (!platform?.id) return [];
      return getRetailproductsByPlatformIdOrders({
        platformId: platform.id,
      });
    },
    staleTime: 1000 * 60 * 2,
    enabled: !!platform?.id,
  });

  const hasAvailableRewards = rewards?.some((r) => r.status === RewardStatus.Available);
  const hasAvailableOffers = promotions?.data?.some(
    (p) => p.expiresOn != null && p.redeemedOn == null,
  );
  // Show Wallet badge for offers (available or redeemed within 2 weeks)
  const hasRedeemedOffers = hasAnyOffersToShow(promotions?.data);
  const hasAvailableMembershipTreatmentsToRedeem = hasAvailableTreatmentsToSelect(
    userMemberships?.data || [],
  );

  const walletLink = user?.isGuest ? "/signin" : "/wallet";

  // Determine if the wallet tab should be visible
  const marketplaceEnabled = platform?.platformSettings?.marketplaceEnabled;
  const hasTreatments = (treatments?.length ?? 0) > 0;
  // Show Offers tab for available or redeemed offers (within 2 weeks)
  const hasPromotions = hasAnyOffersToShow(promotions?.data);
  const hasMemberships = (userMemberships?.data?.length ?? 0) > 0;
  const hasProducts = (productOrders?.length ?? 0) > 0;
  const hasRepeatCashEnabled = platform?.platformSettings?.repeatCashEnabled;

  // Wallet remains visible for owned items even when shop features are disabled.
  const walletTabsVisible =
    marketplaceEnabled ||
    hasTreatments ||
    hasPromotions ||
    hasMemberships ||
    hasProducts ||
    hasRepeatCashEnabled;

  const walletHasBadge = hasRedeemedOffers || hasAvailableMembershipTreatmentsToRedeem;

  const items: PrimaryNavItem[] = [
    {
      key: "home",
      to: "/home",
      label: t("footer.home"),
      svg: HomeNav,
      hasBadge: false,
      intentPreload: preloadHome,
    },
  ];

  if (!user?.isGuest || isAnonymousBrowsing) {
    items.push({
      key: "discover",
      to: "/discover",
      label: t("footer.discover"),
      svg: hasAvailableOffers ? DiscoverPlus1Nav : DiscoverNav,
      hasBadge: !!hasAvailableOffers,
      isActiveOverride: fromWallet ? false : undefined,
      intentPreload: preloadDiscover,
    });
  }

  if (marketplaceEnabled) {
    items.push({
      key: "shop",
      to: "/shop",
      label: t("footer.shop"),
      svg: ShopNav,
      hasBadge: false,
      intentPreload: preloadShop,
    });
  }

  if (platform?.platformSettings?.rewardsEnabled && !isAnonymousBrowsing) {
    items.push({
      key: "rewards",
      to: "/rewards",
      label: t("footer.rewards"),
      svg: hasAvailableRewards ? RewardsPlus1Nav : RewardsNav,
      hasBadge: !!hasAvailableRewards,
      intentPreload: preloadRewards,
    });
  }

  if (walletTabsVisible && !isAnonymousBrowsing) {
    items.push({
      key: "wallet",
      to: walletLink,
      label: t("footer.wallet"),
      svg: walletHasBadge ? WalletPlus1Nav : WalletNav,
      hasBadge: walletHasBadge,
      isActiveOverride: fromWallet ? true : undefined,
      intentPreload: preloadWallet,
    });
  }

  return items;
};
